import { useId, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { apiErrorText } from "@/lib/api-error";
import {
  addCourseStaff,
  fetchCourseStaff,
  removeCourseStaff,
  type CourseStaffMember,
} from "@/services/api/instructor";

type Props = { courseId: string };

/**
 * F1: who teaches this course. Only a course's assigned instructors and TAs (and the school
 * admin) see its students, grade and run its live classes, so the school admin assigns them
 * here. Assigned staff see the list read-only.
 */
export function CourseStaffPanel({ courseId }: Props) {
  const t = useTranslations("courseStaff");
  const tRoot = useTranslations();
  const errorText = (err: unknown, fallbackKey: string) =>
    apiErrorText(err, tRoot, `courseStaff.${fallbackKey}`, { namespace: "courseStaff.errors" });
  const queryClient = useQueryClient();
  const selectId = useId();
  const [selected, setSelected] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const queryKey = ["course-staff", courseId];
  const staffQuery = useQuery({ queryKey, queryFn: () => fetchCourseStaff(courseId) });

  const refresh = () => queryClient.invalidateQueries({ queryKey });

  const addMutation = useMutation({
    mutationFn: (userId: string) => addCourseStaff(courseId, userId),
    onMutate: () => {
      setError(null);
      setNotice(null);
    },
    onSuccess: async (member) => {
      setSelected("");
      setNotice(t("added", { name: displayName(member) }));
      await refresh();
    },
    onError: (err) => setError(errorText(err, "addFailed")),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeCourseStaff(courseId, userId),
    onMutate: () => {
      setError(null);
      setNotice(null);
    },
    onSuccess: refresh,
    onError: (err) => setError(errorText(err, "removeFailed")),
  });

  const roleLabel = (role: CourseStaffMember["role"]) =>
    role === "instructor" ? t("roleInstructor") : t("roleTa");

  function handleRemove(member: CourseStaffMember) {
    if (!window.confirm(t("removeConfirm", { name: displayName(member) }))) return;
    removeMutation.mutate(member.userId);
  }

  const data = staffQuery.data;
  const busy = addMutation.isPending || removeMutation.isPending;

  return (
    <section
      className="space-y-3 rounded-lg border border-[var(--border)] p-4"
      data-testid="course-staff-panel"
      aria-labelledby={`${selectId}-heading`}
    >
      <h3 id={`${selectId}-heading`} className="text-base font-medium">
        {t("title")}
      </h3>
      {staffQuery.isLoading ? (
        <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>
      ) : staffQuery.isError || !data ? (
        <p role="alert" className="text-sm text-red-600" data-testid="course-staff-load-error">
          {errorText(staffQuery.error, "loadFailed")}
        </p>
      ) : (
        <>
          <p className="text-sm text-[var(--text-muted)]">
            {data.canManage ? t("hintAdmin") : t("hintReadOnly")}
          </p>
          {data.staff.length === 0 ? (
            <p className="text-sm" data-testid="course-staff-empty">
              {t("empty")}
            </p>
          ) : (
            <ul className="divide-y divide-[var(--border)]" data-testid="course-staff-list">
              {data.staff.map((member) => (
                <li
                  key={member.userId}
                  className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                  data-testid="course-staff-row"
                >
                  <span>
                    <span className="font-medium">{displayName(member)}</span>{" "}
                    <span className="text-[var(--text-muted)]">
                      · {roleLabel(member.role)}
                      {member.name ? ` · ${member.email}` : null}
                    </span>
                  </span>
                  {data.canManage ? (
                    <button
                      type="button"
                      className="rounded border px-3 py-1 text-sm hover:bg-[var(--surface-3)] disabled:opacity-40"
                      disabled={busy}
                      onClick={() => handleRemove(member)}
                      data-testid="course-staff-remove"
                      aria-label={`${t("remove")}: ${displayName(member)}`}
                    >
                      {t("remove")}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {data.canManage ? (
            data.candidates.length === 0 ? (
              <p
                className="text-sm text-[var(--text-muted)]"
                data-testid="course-staff-no-candidates"
              >
                {t("noCandidates")}
              </p>
            ) : (
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (selected) addMutation.mutate(selected);
                }}
              >
                <label className="block text-sm" htmlFor={selectId}>
                  <span className="text-[var(--text-muted)]">{t("addLabel")}</span>
                  <select
                    id={selectId}
                    className="mt-1 block w-full rounded border px-3 py-2 border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
                    value={selected}
                    onChange={(event) => setSelected(event.target.value)}
                    data-testid="course-staff-select"
                  >
                    <option value="">{t("addPlaceholder")}</option>
                    {data.candidates.map((member) => (
                      <option key={member.userId} value={member.userId}>
                        {`${displayName(member)} · ${roleLabel(member.role)}`}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="submit"
                  className="rounded bg-[var(--ink-12)] px-4 py-2 text-sm text-[var(--ink-1)] disabled:opacity-40"
                  disabled={!selected || busy}
                  data-testid="course-staff-add"
                >
                  {t("add")}
                </button>
              </form>
            )
          ) : null}
        </>
      )}
      {notice ? (
        <p role="status" className="text-sm text-green-700 dark:text-green-400">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-red-600" data-testid="course-staff-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function displayName(member: CourseStaffMember) {
  return member.name?.trim() || member.email;
}
